
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import AiInterviewProfileCard from "@/components/candidate/interviews/ai/AiInterviewProfileCard.vue";
import AiInterviewDetailsCard from "@/components/candidate/interviews/ai/AiInterviewDetailsCard.vue";
import {
  GET_USER_DETAILS,
  USER_PROFILE_BY_ID
} from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import {
  PERSON_INTERVIEW_INVITES,
  INIT_AI_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";
import { get_latest_interview_report } from "@/store/apis/ml_interview";
import { get_job_by_id } from "@/store/apis/common";

export default Vue.extend({
  name: "CandidateFinalInterview",
  components: { AiInterviewProfileCard, AiInterviewDetailsCard },
  data() {
    return {
      coverImage: require("@/assets/images/interviews/ai_interview_cover.png"),
      showTeamsPrejoin: false,
      micEnabled: true,
      camEnabled: true,
      interview: {
        title: "Interview with Person Interviewer",
        date: "",
        formattedDate: "",
        formattedTime: "",
        role: "Candidate",
        format: "Initial Person Interview",
        duration: "30 Minutes",
        transcriptVia: "Microsoft Teams",
        link: "",
        interviewerEmail: "",
        meetingId: "",
        meetingPasscode: "",
        validationAreas: ["HTML", "CSS", "SQL", "Python", "JavaScript", "C#", "Ruby", "Go"]
      },
      personInterviewCompleted: false,
      resolvedJobTitle: "",
      candidateProfile: null as any
    };
  },
  computed: {
    ...mapGetters("auth", { user: GET_USER_DETAILS }),
    ...mapGetters("recruiter_ai_interviews", { personInvites: PERSON_INTERVIEW_INVITES }),
    candidateName(this: any): string {
      if (!this.user?.first_name) return "Interview Candidate";
      return `${this.user.first_name} ${this.user.last_name || ""}`.trim();
    },
    candidateRole(this: any): string {
      const ownRole =
        this.extractCandidateOwnRole(this.candidateProfile) ||
        this.extractCandidateOwnRole(this.user?.profile || this.user?.user_profile || {});
      if (ownRole) return ownRole;
      return (
        this.resolvedJobTitle ||
        this.candidateInvite?.job_title ||
        this.candidateInvite?.job_role ||
        this.candidateInvite?.candidate_role ||
        "Candidate"
      );
    },
    candidateInvite(this: any): any {
      const candidateId = this.user?.id;
      if (!candidateId) return null;
      const inviteId = String(this.$route?.params?.inviteId || "").trim();
      const validInvites = (this.personInvites || []).filter((i: any) => {
        if (i?.candidate_id !== candidateId) return false;
        if (inviteId) return String(i?.id || "") === inviteId;
        if (i?.status !== "scheduled") return false;
        if (!i?.scheduled_at) return false;
        return !isNaN(new Date(i.scheduled_at).getTime());
      });
      validInvites.sort(
        (a: any, b: any) =>
          new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()
      );
      return validInvites[0] ?? null;
    },
    joinLabel(this: any): string {
      if (this.isInterviewExpired && !this.personInterviewCompleted) return "Interview Expired";
      return this.personInterviewCompleted ? "Final Interview Completed" : "Join Final Interview";
    },
    isInterviewExpired(this: any): boolean {
      const scheduledAt = String(this.candidateInvite?.scheduled_at || "").trim();
      if (!scheduledAt) return false;
      const scheduledTs = new Date(scheduledAt).getTime();
      if (!Number.isFinite(scheduledTs)) return false;
      return Date.now() > scheduledTs;
    },
    joinDisabled(this: any): boolean {
      return !this.interview.link || this.personInterviewCompleted || this.isInterviewExpired;
    }
  },
  methods: {
    ...mapActions("auth", {
      fetchProfileById: USER_PROFILE_BY_ID
    }),
    ...mapMutations({ set_root_notification: ROOT_NOTIFICATION }),
    extractCandidateOwnRole(this: any, profileWrapper: any): string {
      const profile = profileWrapper?.profile || profileWrapper || {};
      const workHistory = profile?.work_history_detailed;
      if (!workHistory) return "";
      const entries = Object.values(workHistory || {}) as any[];
      for (const entry of entries) {
        const role = String(entry?.job_title || "").trim();
        if (role) return role;
      }
      return "";
    },
    onViewResume(this: any) {
      this.set_root_notification("Resume preview is not configured yet.");
    },
    onJoinInterview(this: any) {
      if (this.joinDisabled) return;
      const invite = (this as any).candidateInvite;
      if (invite) {
        this.$router.push({
          name: "candidate-final-interview-session",
          query: {
            inviteId: String(invite?.id || ""),
            jobId: String(invite?.job_id || "")
          }
        });
        return;
      }
      const link = (this.interview?.link || "").trim();
      if (link) {
        this.showTeamsPrejoin = true;
        return;
      }
      this.set_root_notification("Meeting link is not available.");
    },
    openTeamsMeeting(this: any) {
      const link = (this.interview?.link || "").trim();
      if (!link) {
        this.set_root_notification("Meeting link is not available.");
        return;
      }
      this.showTeamsPrejoin = false;
      window.location.assign(link);
    },
    formatDateWithOrdinal(this: any, dateIso: string): string {
      const d = new Date(dateIso);
      if (isNaN(d.getTime())) return "";
      const day = d.getDate();
      const month = d.toLocaleString("en-US", { month: "short" });
      const year = d.getFullYear();
      const suffix =
        day % 10 === 1 && day % 100 !== 11
          ? "st"
          : day % 10 === 2 && day % 100 !== 12
            ? "nd"
            : day % 10 === 3 && day % 100 !== 13
              ? "rd"
              : "th";
      return `${day}${suffix} ${month}, ${year}`;
    },
    formatTime(this: any, dateIso: string): string {
      const d = new Date(dateIso);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    },
    async resolveJobTitleById(this: any, jobId: number): Promise<string> {
      if (!jobId) return "";
      try {
        const resp = await get_job_by_id(jobId);
        const payload = resp?.data?.data || resp?.data || {};
        return String(payload?.job_title || payload?.title || "").trim();
      } catch (_e) {
        // ignore
      }
      return "";
    }
  },
  async created(this: any) {
    await this.$store.dispatch("recruiter_ai_interviews/" + INIT_AI_INTERVIEW_INVITES);
    const candidateId = Number(this.user?.id || 0);
    const inviteJobId = Number(this.candidateInvite?.job_id || 0);
    if (candidateId) {
      try {
        this.candidateProfile = await this.fetchProfileById(candidateId);
      } catch (_e) {
        this.candidateProfile = null;
      }
      try {
        const reportResp = await get_latest_interview_report(
          candidateId,
          "person",
          inviteJobId || null
        );
        const report = reportResp?.data?.data || null;
        this.personInterviewCompleted = Boolean(
          report &&
            (report?.status === "completed" ||
              (report?.audit_report && Object.keys(report.audit_report).length > 0))
        );
      } catch (_e) {
        this.personInterviewCompleted = false;
      }
    }
    if (!this.candidateInvite) return;
    this.resolvedJobTitle = await this.resolveJobTitleById(inviteJobId);
    const dIso = this.candidateInvite.scheduled_at;
    this.interview = {
      ...this.interview,
      date: dIso,
      formattedDate: this.formatDateWithOrdinal(dIso),
      formattedTime: this.formatTime(dIso),
      role:
        this.resolvedJobTitle ||
        this.candidateInvite?.job_title ||
        (/^Job\s*#\d+$/i.test(String(this.candidateInvite?.job_role || "").trim())
          ? ""
          : this.candidateInvite?.job_role) ||
        this.candidateInvite?.candidate_role ||
        "Candidate",
      link: this.candidateInvite.ms_teams_link || "",
      interviewerEmail: this.candidateInvite.interviewer_email || "",
      meetingId: this.candidateInvite.ms_teams_join_meeting_id || "",
      meetingPasscode: this.candidateInvite.ms_teams_meeting_passcode || ""
    };
  }
});
